import {
  ACCOUNT_TYPES,
  ACCOUNT_VISIBILITIES,
  CURRENCIES,
  DEFAULT_CURRENCY,
  formatMoney,
  toMinorUnits,
  type AccountVisibility,
} from "@vouch/shared";
import { ChevronDown, ChevronUp, Globe, Lock, Pencil, Plus, Users } from "lucide-react";
import { useState } from "react";
import { Button, Card, Input, Modal, Select, Spinner } from "../components/ui";
import { useAuth } from "../lib/auth";
import {
  useAccounts,
  useAccountTransactions,
  useCreateAccount,
  useUpdateAccount,
  useUpdateAccountVisibility,
  type Account,
  type Transaction,
} from "../lib/hooks";
import { toast } from "../lib/toast";

const VISIBILITY_LABEL: Record<AccountVisibility, string> = {
  private: "Private",
  connections: "Connections",
  public: "Public",
};

function VisibilityIcon({ v }: { v: AccountVisibility }): JSX.Element {
  if (v === "public") return <Globe size={13} />;
  if (v === "connections") return <Users size={13} />;
  return <Lock size={13} />;
}

const EMPTY_FORM = (currency: string): {
  name: string;
  type: string;
  opening: string;
  currency: string;
  visibility: AccountVisibility;
} => ({ name: "", type: "cash", opening: "", currency, visibility: "private" });

export function AccountsPage(): JSX.Element {
  const { user } = useAuth();
  const { data: accounts, isLoading } = useAccounts();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const [open, setOpen] = useState(false);
  // When set, the modal edits this account instead of creating a new one.
  const [editing, setEditing] = useState<Account | null>(null);
  const [form, setForm] = useState(EMPTY_FORM(user?.defaultCurrency ?? DEFAULT_CURRENCY));
  const [error, setError] = useState<string | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setForm(EMPTY_FORM(user?.defaultCurrency ?? DEFAULT_CURRENCY));
    setError(null);
    setOpen(true);
  };

  const openEdit = (a: Account): void => {
    setEditing(a);
    setForm({ name: a.name, type: a.type, opening: "", currency: a.currency, visibility: a.visibility });
    setError(null);
    setOpen(true);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    try {
      if (editing) {
        // Currency is immutable once balances are posted — name/type only.
        await updateAccount.mutateAsync({
          id: editing.id,
          name: form.name.trim(),
          type: form.type,
        });
        setOpen(false);
        toast.success("Account updated");
        return;
      }
      await createAccount.mutateAsync({
        name: form.name.trim(),
        type: form.type as (typeof ACCOUNT_TYPES)[number],
        currency: form.currency,
        openingBalanceMinor: form.opening ? toMinorUnits(Number(form.opening)) : 0,
        visibility: form.visibility,
      });
      setOpen(false);
      toast.success("Account created");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save account");
    }
  };

  if (isLoading) return <Spinner />;

  const busy = createAccount.isPending || updateAccount.isPending;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-500">Accounts</h1>
        <Button onClick={openCreate}>
          <Plus size={16} /> New account
        </Button>
      </div>

      <div className="grid items-start gap-3 sm:grid-cols-2">
        {(accounts ?? []).map((a) => (
          <AccountCard key={a.id} account={a} onEdit={() => openEdit(a)} />
        ))}
        {accounts?.length === 0 && (
          <Card>
            <p className="text-sm text-slate-500">No accounts yet — create your first one.</p>
          </Card>
        )}
      </div>

      <Modal open={open} title={editing ? "Edit account" : "New account"} onClose={() => setOpen(false)}>
        <div className="space-y-3">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            {ACCOUNT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          {editing ? (
            <p className="text-xs text-slate-400">
              Currency ({form.currency}) can't change once an account has transactions.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="Opening balance"
                  type="number"
                  step="0.01"
                  value={form.opening}
                  onChange={(e) => setForm((f) => ({ ...f, opening: e.target.value }))}
                />
                <Select
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </div>
              <Select
                label="Visibility"
                value={form.visibility}
                onChange={(e) =>
                  setForm((f) => ({ ...f, visibility: e.target.value as AccountVisibility }))
                }
              >
                <option value="private">Private — only me</option>
                <option value="connections">Connections — my connections can find it</option>
                <option value="public">Public — anyone who knows my username</option>
              </Select>
            </>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function AccountCard({ account, onEdit }: { account: Account; onEdit: () => void }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const setVisibility = useUpdateAccountVisibility();
  const { data: txs, isLoading } = useAccountTransactions(account.id, expanded);

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-medium text-slate-700">{account.name}</p>
            <button
              onClick={onEdit}
              aria-label="Edit account"
              className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-500"
            >
              <Pencil size={13} />
            </button>
          </div>
          <p className="text-xs uppercase tracking-wide text-slate-400">{account.type}</p>
        </div>
        <p className="shrink-0 text-lg font-semibold text-brand-500">
          {formatMoney(account.cachedBalance, account.currency)}
        </p>
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
        <span className="flex items-center gap-1 text-xs text-slate-400">
          <VisibilityIcon v={account.visibility} /> Visible to
        </span>
        <select
          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs"
          value={account.visibility}
          onChange={(e) =>
            setVisibility
              .mutateAsync({ id: account.id, visibility: e.target.value as AccountVisibility })
              .then(() => toast.success("Visibility updated"))
              .catch(() => toast.error("Failed to update"))
          }
        >
          {ACCOUNT_VISIBILITIES.map((v) => (
            <option key={v} value={v}>
              {VISIBILITY_LABEL[v]}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={() => setExpanded((x) => !x)}
        className="mt-3 flex w-full items-center justify-between border-t border-slate-100 pt-3 text-xs font-medium text-slate-500 hover:text-brand-500"
      >
        Recent activity
        {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>

      {expanded && (
        <div className="mt-2">
          {isLoading ? (
            <p className="py-2 text-xs text-slate-400">Loading…</p>
          ) : txs && txs.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {txs.map((t) => (
                <AccountTxRow key={t.id} t={t} accountId={account.id} />
              ))}
            </ul>
          ) : (
            <p className="py-2 text-xs text-slate-400">No transactions for this account yet.</p>
          )}
        </div>
      )}
    </Card>
  );
}

function AccountTxRow({ t, accountId }: { t: Transaction; accountId: string }): JSX.Element {
  const inflow = t.receiverAccountId === accountId;
  return (
    <li className="flex items-center justify-between gap-2 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm text-slate-700">{t.description || t.type.replace("_", " ")}</p>
        <p className="text-[11px] text-slate-400">{new Date(t.occurredAt).toLocaleDateString()}</p>
      </div>
      <span className={`text-sm font-semibold ${inflow ? "text-green-600" : "text-red-600"}`}>
        {inflow ? "+" : "−"}
        {formatMoney(t.amount, t.currency)}
      </span>
    </li>
  );
}
